/**
 * SELECT 절에서 컬럼 이름(별칭 우선)을 추출해 xField / yFields 로 나눠 반환.
 *
 * 규칙:
 *   - 첫 번째 컬럼  → xField
 *   - 나머지       → yFields
 *   - 주석(--, /* *\/), 문자열 리터럴('...', "..."), 중첩 괄호(SUM(x)) 고려
 *   - alias 우선: `expr AS alias` 또는 `expr alias` (공백 분리 식별자)
 *   - qualifier 제거: `a.col` → `col`
 *
 * MVP 수준 — 서브쿼리 중첩·JOIN·복잡 union 등은 최선만 다함. 실패 시 빈 값 반환.
 */

function splitByTopLevelCommas(s) {
  const out = [];
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  let cur = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === "'" && !inDouble) inSingle = !inSingle;
    else if (ch === '"' && !inSingle) inDouble = !inDouble;
    else if (!inSingle && !inDouble) {
      if (ch === '(') depth++;
      else if (ch === ')') depth--;
      else if (ch === ',' && depth === 0) {
        if (cur.trim()) out.push(cur.trim());
        cur = '';
        continue;
      }
    }
    cur += ch;
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

function stripQuotes(s) {
  if (!s) return s;
  const m = /^(?:"([^"]+)"|'([^']+)'|`([^`]+)`)$/.exec(s);
  return m ? (m[1] || m[2] || m[3]) : s;
}

function extractColumnName(expr) {
  const trimmed = expr.trim();
  if (!trimmed) return '';

  // Case 1: 명시적 AS alias (대소문자 무시)
  //   "..ANY_EXPR.. AS alias"  또는  "..ANY_EXPR.. AS \"Quoted Alias\""
  const asMatch = /\bAS\s+(?:"([^"]+)"|'([^']+)'|`([^`]+)`|([A-Za-z_][A-Za-z0-9_$]*))\s*$/i.exec(trimmed);
  if (asMatch) return asMatch[1] || asMatch[2] || asMatch[3] || asMatch[4];

  // Case 2: 암묵적 alias — 마지막 토큰이 식별자이고 앞에 공백이 있는 경우
  //   "SUM(x) total"  →  "total"
  //   "collected_at t"  →  "t"
  // 문자열·괄호 안의 공백은 무시해야 하므로 "원 expr" 대신 괄호 밖 공백만 본다.
  // 간단화: 끝에서 백트래킹 — 끝 식별자 + 공백 + 괄호/식별자 가 있으면 alias.
  const implicitMatch = /(?:\)|[A-Za-z_0-9$"'`])\s+([A-Za-z_][A-Za-z0-9_$]*)\s*$/.exec(trimmed);
  if (implicitMatch) {
    const last = implicitMatch[1];
    // SQL 예약어는 alias 로 오해하지 않도록 필터
    const reserved = new Set([
      'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'AS', 'ON', 'IN', 'IS', 'NULL',
      'DISTINCT', 'ALL', 'ORDER', 'BY', 'GROUP', 'HAVING', 'ASC', 'DESC',
      'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'OVER', 'PARTITION',
    ]);
    if (!reserved.has(last.toUpperCase())) return last;
  }

  // Case 3: 단순 컬럼 — 마지막 식별자 (테이블 한정자 제거)
  //   "a.col" / "col" / "\"Col\""
  const plainMatch = /(?:[A-Za-z_][A-Za-z0-9_$]*\.)?("[^"]+"|'[^']+'|`[^`]+`|[A-Za-z_][A-Za-z0-9_$]*)\s*$/.exec(trimmed);
  if (plainMatch) return stripQuotes(plainMatch[1]);

  return '';
}

export function parseSelectColumns(sql) {
  if (!sql || typeof sql !== 'string') return { xField: '', yFields: [] };

  // 주석 제거 + 공백 정규화
  const cleaned = sql
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/--[^\n]*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // SELECT ... (FROM|$|WHERE|GROUP|ORDER 등) — 맨 바깥 SELECT 절만 대상
  //   문자열/괄호를 고려한 수작업 스캔으로 FROM 위치 찾기
  const upper = cleaned.toUpperCase();
  if (!upper.startsWith('SELECT ') && !upper.startsWith('SELECT\t')) return { xField: '', yFields: [] };

  // SELECT 이후 시작 인덱스
  const selectStart = 7; // "SELECT "
  // FROM 위치 (depth 0, 문자열 밖)
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  let fromIdx = -1;
  for (let i = selectStart; i < cleaned.length; i++) {
    const ch = cleaned[i];
    if (ch === "'" && !inDouble) { inSingle = !inSingle; continue; }
    if (ch === '"' && !inSingle) { inDouble = !inDouble; continue; }
    if (inSingle || inDouble) continue;
    if (ch === '(') { depth++; continue; }
    if (ch === ')') { depth--; continue; }
    if (depth !== 0) continue;
    // FROM 키워드 체크 (단어 경계)
    if ((ch === 'F' || ch === 'f') && upper.startsWith('FROM', i) && !/[A-Za-z0-9_$]/.test(cleaned[i - 1] || ' ') && !/[A-Za-z0-9_$]/.test(cleaned[i + 4] || ' ')) {
      fromIdx = i;
      break;
    }
  }

  let selectPart;
  if (fromIdx < 0) {
    // FROM 없는 쿼리 (드물지만 허용)
    selectPart = cleaned.substring(selectStart);
  } else {
    selectPart = cleaned.substring(selectStart, fromIdx).trim();
  }

  // DISTINCT / ALL / UNIQUE / TOP n 접두어 제거
  selectPart = selectPart.replace(/^(DISTINCT|ALL|UNIQUE)\s+/i, '');
  selectPart = selectPart.replace(/^TOP\s+\d+\s+/i, '');

  const cols = splitByTopLevelCommas(selectPart);
  const names = cols.map(extractColumnName).filter(Boolean);

  return {
    xField: names[0] || '',
    yFields: names.slice(1),
  };
}
